import { useState } from 'react';
import { Lock } from 'lucide-react';
import PlusUpsellModal from './PlusUpsellModal';

/**
 * DividendFilterSegmentedControl — Round 95z (Jun 2026), Section 4
 * of the Non-Dividend Stock Expansion.
 *
 * 3-way pill control that flips the Stock Table between:
 *   • "dividend"     — dividend payers only (default, all users)
 *   • "all"          — full universe (admin / paid only)
 *   • "non_dividend" — non-dividend stocks only (admin / paid only)
 *
 * Round 96f (Feb 2026) — Lock icon is now Challenge-yellow (matches
 * the Challenges dropdown's `text-yellow-500 / dark:text-yellow-400`
 * accent). For free users, clicking a locked segment opens the
 * Round 104g (Jun 2026) — For free users, clicking a locked segment
 * opens PlusUpsellModal — the DiviDrip Plus sales pitch covering the
 * full Plus feature set (full universe, quant/forensic analytics,
 * AI research, ownership intelligence, power tools).
 *
 * Admins (and future paid users) see no lock and switch normally.
 * URL state isn't owned here; the parent manages it via `?filter=`
 * and passes the active value down.
 */

const FILTER_OPTIONS = [
  { value: 'dividend', label: 'Dividend', requiresAccess: false },
  { value: 'all', label: 'All', requiresAccess: true },
  { value: 'non_dividend', label: 'Non-Dividend', requiresAccess: true },
];

export default function DividendFilterSegmentedControl({
  value = 'dividend',
  onChange,
  isAdmin = false,
  instanceId,
}) {
  // When the component appears multiple times on a page (desktop +
  // mobile copies, sidebar widgets), `instanceId` makes each testid
  // unique so test scripts can target the visible instance reliably.
  const suffix = instanceId ? `-${instanceId}` : '';
  const [showComingSoon, setShowComingSoon] = useState(false);

  return (
    <>
      <div
        className="inline-flex items-center rounded-full border border-border/60 bg-card p-1"
        role="radiogroup"
        aria-label="Filter stocks by dividend status"
        data-testid={`dividend-filter-segmented-control${suffix}`}
      >
        {FILTER_OPTIONS.map((opt) => {
          const isActive = value === opt.value;
          const isLocked = opt.requiresAccess && !isAdmin;
          const handleClick = () => {
            if (isLocked) {
              setShowComingSoon(true);
              return;
            }
            if (onChange && !isActive) onChange(opt.value);
          };
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={isActive}
              onClick={handleClick}
              data-testid={`dividend-filter-option-${opt.value}${suffix}`}
              title={
                isLocked
                  ? 'Locked — DiviDrip Plus feature'
                  : `Show ${opt.label.toLowerCase()} stocks`
              }
              className={[
                'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full transition-colors',
                isActive
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/40',
              ].join(' ')}
            >
              {isLocked && (
                <Lock
                  className="w-3 h-3 text-yellow-500 dark:text-yellow-400"
                  aria-hidden="true"
                  data-testid={`dividend-filter-lock-${opt.value}${suffix}`}
                />
              )}
              {opt.label}
            </button>
          );
        })}
      </div>
      <PlusUpsellModal
        open={showComingSoon}
        onClose={() => setShowComingSoon(false)}
      />
    </>
  );
}
